/**
 * CompanyDashboard.jsx — Week 2
 * Route: /company
 * Shows tenant overview: company info, plan, key metrics,
 * recent decisions across all campaigns, usage, quick actions.
 * 
 * w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-700/40 hover:bg-gray-700/80 transition-colors
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { functions, db } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { collection, query, where, getDocs, orderBy, limit } from "firebase/firestore";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { channelDisplay } from "../lib/channels";
import { useFeatureFlags } from "../hooks/useFeatureFlags";
import { useRoles } from "../hooks/useRoles";
import Navigation from "../components/layout/Navigation";

import { useDarkMode } from "../hooks/useDarkMode";
 import {FiTarget, FiTrendingUp, FiCalendar, FiCheckCircle,
 FiXCircle, FiPauseCircle, FiPlus, FiArrowRight,
 FiSettings, FiBarChart2, FiUsers, FiZap, FiStar,
 FiActivity, FiClock, FiRefreshCw,
} from "react-icons/fi";

/* ── helpers ──────────────────────────────────────────────── */
// Decision style map.
// The current backend (evaluateCampaign in functions/index.js) normalizes
// PAUSE → ADJUST before saving, so the canonical key is ADJUST. PAUSE is
// kept as an alias so legacy decisions saved before the normalize was
// deployed still render correctly.
const DECISION_STYLES = {
 CONTINUE: { icon: <FiCheckCircle />, cls: "text-green-400", bg: "bg-green-500/10 border-green-500/30" },
 ADJUST:   { icon: <FiPauseCircle />, cls: "text-yellow-400", bg: "bg-yellow-500/10 border-yellow-500/30" },
 PAUSE:    { icon: <FiPauseCircle />, cls: "text-yellow-400", bg: "bg-yellow-500/10 border-yellow-500/30" },
 STOP: { icon: <FiXCircle />, cls: "text-red-400", bg: "bg-red-500/10 border-red-500/30" },
};

const CHANNEL_COLORS = {
 tiktok: "bg-pink-500/20 text-pink-400 border-pink-500/40",
 snapchat: "bg-yellow-500/20 text-yellow-400 border-yellow-500/40",
 google: "bg-blue-500/20 text-blue-400 border-blue-500/40",
 meta: "bg-indigo-500/20 text-indigo-400 border-indigo-500/40",
 other: "bg-gray-500/20 text-gray-400 border-gray-500/40",
};

const PLAN_STYLES = {
 free: { label: "Free", cls: "bg-gray-700 text-gray-300 border-gray-600" },
 pro: { label: "Pro", cls: "bg-blue-500/20 text-blue-400 border-blue-500/40" },
 enterprise: { label: "Enterprise", cls: "bg-purple-500/20 text-purple-400 border-purple-500/40" },
};

const BIZ_ICONS = { agency: "🎯", store: "🛒", service: "💼" };


/* ── Bilingual helpers ────────────────────────────────────── */
const NEXT_ACTIONS_AR = {
  'Scale budget 20-30% to maximize returns':      'زِد الميزانية 20-30% لتعظيم العائد',
  'Continue A/B testing creatives':               'استمر في اختبار A/B للإعلانات',
  'Monitor daily and adjust bids':                'راقب يومياً وعدّل العروض',
  'Monitor top-performing products':              'راقب المنتجات الأعلى أداءً',
  'Implement upselling strategies':               'طبّق استراتيجيات البيع الإضافي',
  'Review inventory levels':                      'راجع مستويات المخزون',
  'Optimize lead generation channels':            'حسّن قنوات توليد العملاء المحتملين',
  'Invest in customer retention':                 'استثمر في الاحتفاظ بالعملاء',
  'Track LTV by segment':                         'تتبع القيمة الدائمة حسب الشريحة',
  'Pause campaign and analyze data':              'أوقف الحملة مؤقتاً وحلّل البيانات',
  'Review targeting and audience':                'راجع الاستهداف والجمهور',
  'Test new creatives before resuming':           'اختبر إعلانات جديدة قبل الاستئناف',
  'Evaluate budget allocation':                   'قيّم توزيع الميزانية',
  'Stop campaign immediately':                    'أوقف الحملة فوراً',
  'Conduct post-mortem analysis':                 'أجرِ تحليلاً شاملاً للأداء',
  'Revise strategy before relaunch':              'راجع الاستراتيجية قبل إعادة الإطلاق',
  'Analyze what failed for future campaigns':     'حلّل أسباب الفشل للحملات المستقبلية',
};
function getLocalizedReason(dec, lang) {
  if (lang === 'ar') return dec.reasonAr || dec.reason || '';
  return dec.reason || '';
}

function formatDate(ts, locale) {
 try {
 const d = ts?.seconds ? new Date(ts.seconds * 1000)
 : ts?.toDate ? ts.toDate()
 : ts instanceof Date ? ts
 : typeof ts === "string" || typeof ts === "number" ? new Date(ts) : null;
 if (!d || isNaN(d.getTime())) return "—";
 return d.toLocaleDateString(locale, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
 } catch { return "—"; }
}

function timeAgo(ts, isRTL) {
 try {
 const d = ts?.seconds ? new Date(ts.seconds * 1000) : ts instanceof Date ? ts : new Date(ts);
 const diff = Math.floor((Date.now() - d.getTime()) / 1000);
 if (diff < 60) return isRTL ? "الآن" : "just now";
 if (diff < 3600) return isRTL ? `${Math.floor(diff/60)} د` : `${Math.floor(diff/60)}m ago`;
 if (diff < 86400)return isRTL ? `${Math.floor(diff/3600)} س` : `${Math.floor(diff/3600)}h ago`;
 return isRTL ? `${Math.floor(diff/86400)} ي` : `${Math.floor(diff/86400)}d ago`;
 } catch { return "—"; }
}

/* ─────────────────────────────────────────────────────────── */
const CompanyDashboard = () => {
 const navigate = useNavigate();
 const { userData, tenant, planId } = useAuth();
 const { t, isRTL, lang } = useLanguage();
 const { features } = useFeatureFlags();
 const { canAccessAdmin } = useRoles();
 const { darkMode } = useDarkMode();

 const [campaigns, setCampaigns] = useState([]);
 const [recentDecisions, setRecentDecisions] = useState([]);
 const [usageStats, setUsageStats] = useState(null);
 const [loading, setLoading] = useState(true);
 const [refreshing, setRefreshing] = useState(false);
 const [error, setError] = useState("");

 const locale = lang === "ar" ? "ar-SA" : "en-US";

 /* ── data loading ─────────────────────────────────────── */
 const loadAll = useCallback(async (isRefresh = false) => {
 // Bail without flipping loading=false. Keeping loading=true means the
 // skeleton stays visible until tenantId is available and the real
 // fetch runs — preventing the brief flash of empty state on login.
 if (!userData?.tenantId) return;
 if (isRefresh) setRefreshing(true); else setLoading(true);
 setError("");

 try {
 // Load campaigns
 let camps = [];
 try {
 const fn = httpsCallable(functions, "getCampaigns");
 const res = await fn();
 camps = res.data.campaigns || [];
 } catch {
 const q = query(
 collection(db, "campaigns"),
 where("tenantId", "==", userData.tenantId),
 orderBy("createdAt", "desc")
 );
 const snap = await getDocs(q);
 camps = snap.docs.map(d => ({ id: d.id, ...d.data() }));
 }
 setCampaigns(camps);

 // Load recent decisions across all campaigns (last 10).
 // We used to try a `getRecentDecisions` Cloud Function first with
 // a Firestore fallback in catch — but that function was never
 // implemented on the backend, so every page load wasted a round
 // trip and spammed the console with CORS errors (Firebase Functions
 // returns 404 for unknown function names; the browser surfaces that
 // as a CORS-preflight failure because there are no CORS headers on
 // the 404). Going straight to Firestore is faster and quieter — and
 // the security rules already restrict `decisions` reads to the
 // tenant's own data.
 try {
 const q2 = query(
 collection(db, "decisions"),
 where("tenantId", "==", userData.tenantId),
 orderBy("createdAt", "desc"),
 limit(10)
 );
 const snap2 = await getDocs(q2);
 setRecentDecisions(snap2.docs.map(d => ({ id: d.id, ...d.data() })));
 } catch { setRecentDecisions([]); }

 // Load usage stats
 try {
 const fn3 = httpsCallable(functions, "getUsageStats");
 const res3 = await fn3({ tenantId: userData.tenantId });
 setUsageStats(res3.data);
 } catch { setUsageStats(null); }

 } catch (err) {
 setError(err.message || t("error"));
 } finally {
 setLoading(false);
 setRefreshing(false);
 }
 }, [userData?.tenantId]);

 // Only fire loadAll once tenantId is available. While it isn't, loading
 // stays at its initial true value and the skeleton remains visible —
 // no flicker of empty content.
 useEffect(() => {
   if (!userData?.tenantId) return;
   loadAll();
 }, [userData?.tenantId, loadAll]);

 /* ── derived stats ─────────────────────────────────────── */
 const totalCampaigns = campaigns.length;
 const activeCampaigns = campaigns.filter(c => c.status === "active").length;
 const totalEvaluations = campaigns.reduce((s, c) => s + (c.decisionsCount || 0), 0);
 const evThisMonth = usageStats?.usage?.evaluations || 0;
 // Canonical plan tier — comes from AuthContext which derives it from
 // tenant.planId (or the `plan` alias) with a legacy subscriptionTier
 // fallback. The previous direct read of userData.subscriptionTier was
 // a phantom field that always came back undefined, so the badge here
 // showed "Free" for every tenant regardless of actual plan.
 const planTier = planId;
 const planStyle = PLAN_STYLES[planTier] || PLAN_STYLES.free;
 const bizType = tenant?.businessType || userData?.businessType || "agency";

 // Aggregate decision counts. We fold any legacy "PAUSE" entries into
 // "ADJUST" so the breakdown reflects all decisions regardless of when
 // they were saved (before or after the backend normalize).
 const decisionCounts = recentDecisions.reduce((acc, d) => {
 const key = d.decision === "PAUSE" ? "ADJUST" : d.decision;
 acc[key] = (acc[key] || 0) + 1;
 return acc;
 }, {});

 // Aggregate campaigns by the status of their LATEST decision. Unlike
 // decisionCounts above (which counts the last 10 decisions and ignores
 // whether they belong to the same campaign), this counts each campaign
 // exactly once. The 4th bucket "PENDING" covers campaigns that have
 // never been evaluated — these are invisible to decisionCounts because
 // they have no decision document at all.
 const campaignStatusCounts = campaigns.reduce((acc, c) => {
   const raw = c.latestDecision?.decision;
   const key = raw === "PAUSE" ? "ADJUST" : (raw || "PENDING");
   acc[key] = (acc[key] || 0) + 1;
   return acc;
 }, {});

 /* ── loading skeleton ──────────────────────────────────── */
 if (loading) return (
 <div className={`min-h-screen ${darkMode ? "bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white" : "bg-gradient-to-br from-gray-50 via-white to-gray-100 text-gray-900"}`}>
 <Navigation />
 <div className="p-4 md:p-8 max-w-7xl mx-auto">
 <div className="animate-pulse space-y-6">
 <div className={`h-32 ${darkMode ? "bg-gray-800" : "bg-gray-200"} rounded-2xl`} />
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
 {[...Array(4)].map((_, i) => <div key={i} className={`h-24 ${darkMode ? "bg-gray-800" : "bg-gray-200"} rounded-xl`} />)}
 </div>
 <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
 <div className={`lg:col-span-2 h-96 ${darkMode ? "bg-gray-800" : "bg-gray-200"} rounded-2xl`} />
 <div className={`h-96 ${darkMode ? "bg-gray-800" : "bg-gray-200"} rounded-2xl`} />
 </div>
 </div>
 </div>
 </div>
 );

 /* ─────────────────── RENDER ─────────────────────────── */
 return (
 <div
 
 className={`min-h-screen ${darkMode ? "bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white" : "bg-gradient-to-br from-gray-50 via-white to-gray-100 text-gray-900"}`}
 >
 <Navigation />

 <div className="p-4 md:p-8 max-w-7xl mx-auto">

 {/* ── Error banner ─────────────────────────────── */}
 {error && (
 <div className={`mb-6 bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-xl flex items-center justify-between `}>
 <span>{error}</span>
 <button onClick={() => loadAll(true)} className="underline text-sm">{t("try_again")}</button>
 </div>
 )}

 {/* ══════════════════════════════════════════════
 COMPANY HERO HEADER
 ══════════════════════════════════════════════ */}
 <motion.div
 initial={{ opacity: 0, y: -20 }}
 animate={{ opacity: 1, y: 0 }}
 className={`relative ${darkMode ? "bg-gradient-to-r from-gray-800 to-gray-800/50 border-gray-700" : "bg-white border-gray-200"} border rounded-2xl p-6 md:p-8 mb-8 overflow-hidden`}
 >
 {/* Background decoration */}
 <div className="absolute inset-0 overflow-hidden pointer-events-none">
 <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl" />
 <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-purple-600/10 rounded-full blur-3xl" />
 </div>

 <div className={`relative flex flex-col  md:flex-row md:items-center justify-between gap-4 `}>
 {/* Left: Company info */}
 <div >
 <div className={`flex items-center gap-3 mb-2 `}>
 <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-2xl font-bold text-white shadow-lg">
 {(tenant?.companyName || userData?.displayName || "C")[0].toUpperCase()}
 </div>
 <div>
 <h1 className={`text-2xl md:text-3xl font-bold ${darkMode ? "text-white" : "text-gray-900"} leading-tight`}>
 {tenant?.companyName || userData?.displayName || t("company_dashboard")}
 </h1>
 <div className={`flex items-center gap-2 mt-1 `}>
 <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${planStyle.cls}`}>
 {/* Localised plan label — uses translation keys plan_free/plan_pro/
     plan_enterprise so AR users see مجاني / احترافي / المؤسسات. The
     emoji is decorative and language-agnostic. */}
 {planTier === "pro"        ? `💎 ${t("plan_pro")}` :
  planTier === "enterprise" ? `⭐ ${t("plan_enterprise")}` :
                              t("plan_free")}
 </span>
 <span className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-sm`}>
 {BIZ_ICONS[bizType]} {t(`business_type_${bizType}`) || bizType}
 </span>
 </div>
 </div>
 </div>
 <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-sm`}>
 {t("welcome_back")}, <span className={`${darkMode ? "text-white" : "text-gray-900"} font-medium`}>{userData?.displayName || userData?.email}</span>
 {" · "}{t("role_label")}: <span className="text-blue-400">{t(`role_${userData?.role || "member"}`)}</span>
 </p>
 </div>

 {/* Right: Quick actions */}
 <div className={`flex items-center gap-3 flex-shrink-0 `}>
 <button
 onClick={() => loadAll(true)}
 disabled={refreshing}
 className={`p-2.5 ${darkMode ? "bg-gray-700 hover:bg-gray-600 text-gray-300" : "bg-gray-200 hover:bg-gray-300 text-gray-600"} rounded-xl transition-colors`}
 title={t("refresh")}
 >
 <FiRefreshCw className={refreshing ? "animate-spin" : ""} />
 </button>
 <button
 onClick={() => navigate("/campaigns")}
 className={`flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-semibold transition-colors `}
 >
 <FiPlus />{t("new_campaign")}
 </button>
 </div>
 </div>
 </motion.div>

 {/* ══════════════════════════════════════════════
 KEY METRICS ROW {t(decision_${dec.decision?.toLowerCase()})}
 ══════════════════════════════════════════════ */}
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
 {[
 {
 icon: <FiTarget className="text-blue-400 text-xl" />,
 label: t("total_campaigns"),
 value: totalCampaigns,
 sub: `${activeCampaigns} ${t("active_label")}`,
 subCls: "text-green-400",
 onClick: () => navigate("/campaigns"),
 },
 {
 icon: <FiActivity className="text-purple-400 text-xl" />,
 label: t("evaluations_this_month"),
 value: evThisMonth,
 sub: usageStats ? `${t("of_label")} ${usageStats.quotas?.evaluations === -1 ? "∞" : (usageStats.quotas?.evaluations || "—")}` : "—",
 subCls: "text-gray-400",
 },
 {
 icon: <FiCheckCircle className="text-green-400 text-xl" />,
 label: t("continue_decisions"),
 value: decisionCounts.CONTINUE || 0,
 sub: t("last_10_decisions"),
 subCls: "text-gray-400",
 },
 {
 icon: <FiZap className="text-yellow-400 text-xl" />,
 label: t("plan_label"),
 value: t(`plan_${planTier}`),
 sub: totalEvaluations + " " + t("total_evals_label"),
 subCls: "text-gray-400",
 onClick: () => navigate("/admin/plans"),
 },
 ].map((stat, i) => (
 <motion.div
 key={i}
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: i * 0.08 }}
 onClick={stat.onClick}
 className={`${darkMode ? "bg-gray-800/50 border-gray-700" : "bg-white border-gray-200"} border rounded-xl p-5 ${stat.onClick ? "cursor-pointer hover:border-blue-500 transition-colors" : ""}`}
 >
 <div className={`flex items-start gap-3 `}>
 <div className={`p-2 ${darkMode ? "bg-gray-700/60" : "bg-gray-100"} rounded-lg flex-shrink-0`}>{stat.icon}</div>
 <div>
 <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-xs mb-1`}>{stat.label}</p>
 <p className={`${darkMode ? "text-white" : "text-gray-900"} text-2xl font-bold leading-none mb-1`}>{stat.value}</p>
 <p className={`text-xs ${stat.subCls || (darkMode ? "text-gray-400" : "text-gray-500")} $`}>{stat.sub}</p>
 </div>
 </div>
 </motion.div>
 ))}
 </div>

 {/* ══════════════════════════════════════════════
 MAIN CONTENT GRID
 ══════════════════════════════════════════════ */}
 <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

 {/* ── LEFT/MAIN: Recent Decisions Feed ─────── */}
 <motion.div
 initial={{ opacity: 0, x: -20 }}
 animate={{ opacity: 1, x: 0 }}
 className={`lg:col-span-2 ${darkMode ? "bg-gray-800/30" : "bg-white"} border ${darkMode ? "border-gray-700" : "border-gray-200"} rounded-2xl p-6` }
 >
 <div className={`flex items-center justify-between mb-6 `}>
 <div className={`flex items-center gap-2 `}>
 <FiClock className="text-blue-400 text-lg" />
 <h2 className={`text-lg font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>{t("recent_activity")}</h2>
 </div>
 <button
 onClick={() => navigate("/campaigns")}
 className={`flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm transition-colors `}
 >
 {t("view_all")} <FiArrowRight className={isRTL ? "rotate-180" : ""} />
 </button>
 </div>

 {recentDecisions.length === 0 ? (
 <div className="text-center py-12">
 <FiActivity className={`${darkMode ? "text-gray-600" : "text-gray-400"} text-5xl mx-auto mb-3`} />
 <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-sm mb-4`}>{t("no_recent_activity")}</p>
 <button
 onClick={() => navigate("/campaigns")}
 className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors"
 >
 {t("go_to_campaigns")}
 </button>
 </div>
 ) : (
 <div className="space-y-3">
 {recentDecisions.map((dec, i) => {
 const style = DECISION_STYLES[dec.decision] || DECISION_STYLES.CONTINUE;
 const chColor = CHANNEL_COLORS[dec.channel] || CHANNEL_COLORS.other;
 return (
 <motion.div
 key={dec.id || i}
 initial={{ opacity: 0, y: 10 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: i * 0.05 }}
 onClick={() => dec.campaignId && navigate(`/campaigns/${dec.campaignId}`)}
 className={`flex items-start gap-4 p-4 rounded-xl border ${style.bg} cursor-pointer hover:opacity-90 transition-opacity `}
 >
 <div className={`text-xl flex-shrink-0 mt-0.5 ${style.cls}`}>{style.icon}</div>
 <div className={`flex-1 min-w-0 $`}>
 <div className={`flex items-center gap-2 mb-1 flex-wrap `}>
<span className={`font-semibold ${style.cls}`}>{t(`decision_${dec.decision?.toLowerCase()}`)}</span>
 {dec.campaignName && (
 <span className={`${darkMode ? "text-white" : "text-gray-900"} text-sm font-medium truncate`}>{dec.campaignName}</span>
 )}
 {dec.channel && (
 <span className={`text-xs px-2 py-0.5 rounded-full border ${chColor} capitalize`}>
 {channelDisplay(dec.channel, lang)}
 </span>
 )}
 </div>
                        {dec.reason && (() => {
                          const localizedReason = getLocalizedReason(dec, lang);
                          const showEnBadge = lang === 'ar' && !dec.reasonAr;
                          return (
                            <p
                              className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-xs line-clamp-2`}
                              dir={showEnBadge ? "ltr" : undefined}
                            >
                              {showEnBadge && <span className="inline-block bg-gray-700 text-gray-400 text-xs px-1.5 py-0.5 rounded mr-1">EN</span>}
                              {localizedReason}
                            </p>
                          );
                        })()}
 </div>
 <span className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs flex-shrink-0 mt-0.5`}>
 {timeAgo(dec.createdAt, isRTL)}
 </span>
 </motion.div>
 );
 })}
 </div>
 )}
 </motion.div>

 {/* ── RIGHT: Sidebar ───────────────────────── */}
 <div className="space-y-5">

 {/* Decision breakdown donut */}
 <motion.div
 initial={{ opacity: 0, x: 20 }}
 animate={{ opacity: 1, x: 0 }}
 className={`${darkMode ? "bg-gray-800/30" : "bg-white"} border ${darkMode ? "border-gray-700" : "border-gray-200"} rounded-2xl p-6` }
 >
 <div className={`flex items-center gap-2 mb-5 `}>
 <FiBarChart2 className="text-blue-400" />
 <h3 className={`font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}>{t("decision_breakdown")}</h3>
 </div>

 {/* Empty state guard now uses totalCampaigns since the widget is
     campaign-centric rather than decision-centric. */}
 {totalCampaigns === 0 ? (
 <p className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-sm text-center py-4`}>{t("no_data")}</p>
 ) : (
 <div className="space-y-3">
 {[
 { key: "CONTINUE", label: t("decision_continue"), color: "bg-green-500", textCls: "text-green-400" },
 // Backend stores ADJUST; we fold legacy PAUSE into ADJUST in
 // campaignStatusCounts above so this single row covers both.
 { key: "ADJUST",   label: t("decision_adjust"),   color: "bg-yellow-500", textCls: "text-yellow-400" },
 { key: "STOP", label: t("decision_stop"), color: "bg-red-500", textCls: "text-red-400" },
 // 4th bar: campaigns that have never been evaluated. With this
 // bar, the four percentages sum to 100% of total campaigns and
 // the breakdown matches the stat cards on /campaigns.
 { key: "PENDING",  label: t("status_pending"),    color: "bg-gray-400",  textCls: darkMode ? "text-gray-300" : "text-gray-500" },
 ].map(({ key, label, color, textCls }) => {
 const count = campaignStatusCounts[key] || 0;
 const pct = totalCampaigns > 0 ? Math.round((count / totalCampaigns) * 100) : 0;
 return (
 <div key={key}>
 <div className={`flex justify-between text-sm mb-1 `}>
 <span className={textCls}>{label}</span>
 <span className={`${darkMode ? "text-gray-400" : "text-gray-500"} tabular-nums`}>{count} ({pct}%)</span>
 </div>
 <div className={`h-2 ${darkMode ? "bg-gray-700" : "bg-gray-200"} rounded-full overflow-hidden`}>
 <motion.div
 initial={{ width: 0 }}
 animate={{ width: `${pct}%` }}
 transition={{ duration: 0.8, delay: 0.2 }}
 className={`h-full ${color} rounded-full`}
 />
 </div>
 </div>
 );
 })}
 <p className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs pt-1`}>
 {/* Subtitle now reads "based on N campaigns" because the bars are
     campaign-centric. */}
 {t("based_on_label")} {totalCampaigns} {t("total_campaigns").toLowerCase()}
 </p>
 </div>
 )}
 </motion.div>

 {/* Usage at a glance */}
 {usageStats && (
 <motion.div
 initial={{ opacity: 0, x: 20 }}
 animate={{ opacity: 1, x: 0 }}
 transition={{ delay: 0.1 }}
 className={`${darkMode ? "bg-gray-800/30" : "bg-white"} border ${darkMode ? "border-gray-700" : "border-gray-200"} rounded-2xl p-6` }
 >
 <div className={`flex items-center gap-2 mb-4 `}>
 <FiTrendingUp className="text-purple-400" />
 <h3 className={`font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}>{t("usage_this_month")}</h3>
 </div>
 {(() => {
 const ev = usageStats.usage?.evaluations || 0;
 const max = usageStats.quotas?.evaluations;
 const unlim = max === -1;
 const pct = unlim ? 0 : max > 0 ? Math.min((ev / max) * 100, 100) : 0;
 const barCls = pct >= 90 ? "bg-red-500" : pct >= 75 ? "bg-yellow-500" : "bg-blue-500";
 return (
 <div className="space-y-3">
 <div>
 <div className={`flex justify-between text-sm mb-1 `}>
 <span className={`${darkMode ? "text-gray-300" : "text-gray-700"}`}>{t("campaign_evaluations")}</span>
 <span className={`${darkMode ? "text-white" : "text-gray-900"} font-semibold`}>
 {ev}{unlim ? "" : ` / ${max}`}
 </span>
 </div>
 {!unlim && max > 0 && (
 <div className={`h-2 ${darkMode ? "bg-gray-700" : "bg-gray-200"} rounded-full overflow-hidden`}>
 <motion.div
 initial={{ width: 0 }}
 animate={{ width: `${pct}%` }}
 transition={{ duration: 0.8 }}
 className={`h-full ${barCls} rounded-full`}
 />
 </div>
 )}
 {unlim && <div className="text-green-400 text-xs mt-1">{t("unlimited_evaluations")}</div>}
 </div>
 {pct >= 80 && (
 <button
 onClick={() => navigate("/admin/plans")}
 className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-xl text-sm font-semibold transition-colors"
 >
 {t("upgrade_now")}
 </button>
 )}
 </div>
 );
 })()}
 </motion.div>
 )}

 {/* Quick links */}
 <motion.div
 initial={{ opacity: 0, x: 20 }}
 animate={{ opacity: 1, x: 0 }}
 transition={{ delay: 0.15 }}
 className={`${darkMode ? "bg-gray-800/30" : "bg-white"} border ${darkMode ? "border-gray-700" : "border-gray-200"} rounded-2xl p-6` }
 >
 <h3 className={`font-semibold mb-4 ${darkMode ? "text-white" : "text-gray-900"} $`}>{t("quick_actions")}</h3>
 <div className="space-y-2">
 {[
 { icon: <FiTarget />, label: t("nav_campaign_decision"), path: "/campaigns", cls: "text-blue-400" },
 { icon: <FiSettings />, label: t("nav_admin"), path: "/admin", cls: "text-gray-400", adminOnly: true },
 { icon: <FiStar />, label: t("plans_title"), path: "/admin/plans", cls: "text-yellow-400" },
 { icon: <FiUsers />, label: t("manage_team"), path: "/admin", cls: "text-purple-400", adminOnly: true },
 ].filter(l => !l.adminOnly || canAccessAdmin())
 .map((link, i) => (
 <button
 key={i}
 onClick={() => navigate(link.path)}
className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors
  ${darkMode ? "bg-gray-700/40 hover:bg-gray-700/80" : "bg-gray-100 hover:bg-gray-200"}`}
 >
  {/* text-gray-500 text-xs */}
 <span className={link.cls}>{link.icon}</span>
 <span className={`${darkMode ? "text-gray-200" : "text-gray-700"} text-sm font-medium flex-1 $ `}>{link.label}</span>
 <FiArrowRight className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`} />
 </button>
 ))}
 </div>
 </motion.div>

 {/* Campaign list mini w-full flex items-center gap-3 p-3 rounded-xl bg-gray-700/30 hover:bg-gray-700/60 transition-colors */}
 {campaigns.length > 0 && (
 <motion.div
 initial={{ opacity: 0, x: 20 }}
 animate={{ opacity: 1, x: 0 }}
 transition={{ delay: 0.2 }}
 className={`${darkMode ? "bg-gray-800/30" : "bg-white"} border ${darkMode ? "border-gray-700" : "border-gray-200"} rounded-2xl p-6` }
 >
 <div className={`flex items-center justify-between mb-4 `}>
 <h3 className={`font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}>{t("your_campaigns")}</h3>
 <span className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs`}>{campaigns.length}</span>
 </div>
 <div className="space-y-2">
 {campaigns.slice(0, 5).map(c => {
 const chCls = CHANNEL_COLORS[c.channel] || CHANNEL_COLORS.other;
 const decCls = DECISION_STYLES[c.latestDecision?.decision];
 return (
 <button
 key={c.id}
 onClick={() => navigate(`/campaigns/${c.id}`)}
 className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors
  ${darkMode ? "bg-gray-700/30 hover:bg-gray-700/60" : "bg-gray-50 hover:bg-gray-100 border border-gray-200"}`}
 >
 <div className={`w-2 h-2 rounded-full flex-shrink-0 ${c.status === "active" ? "bg-green-400" : "bg-gray-500"}`} />
 <span className={`${darkMode ? "text-gray-200" : "text-gray-700"} text-sm flex-1 truncate $ `}>{c.name}</span>
 <div className={`flex items-center gap-1.5 flex-shrink-0 `}>
 {c.channel && <span className={`text-xs px-1.5 py-0.5 rounded border ${chCls}`}>{channelDisplay(c.channel, lang)}</span>}
 {c.latestDecision?.decision && decCls && (
 <span className={`text-xs ${decCls.cls}`}>{decCls.icon}</span>
 )}
 </div>
 </button>
 );
 })}
 {campaigns.length > 5 && (
 <button
 onClick={() => navigate("/campaigns")}
 className={`w-full text-center text-blue-400 text-sm py-2 hover:text-blue-300 transition-colors`}
 >
 +{campaigns.length - 5} {t("more_campaigns")}
 </button>
 )}
 </div>
 </motion.div>
 )}
 </div>
 </div>
 </div>
 </div>
 );
};

export default CompanyDashboard;